<template>
  <div class="list-lead">
    <KTCodePreview v-bind:title="'Danh sách Leads'">
      <!-- Toolbar Section -->
      <template v-slot:toolbar v-if="haveHeaderActions">
        <b-row>
          <b-dropdown size="sm" right v-show="canImport || canExport">
            <template slot="button-content">
              <i style="font-size: 1rem" class="fas fa-cog"></i>
              <span class="font-weight-bolder">Thao tác</span>
            </template>
            <b-dropdown-item @click="exportExcel" v-show="canExport">
              <span>
                <i style="font-size: 1rem" class="far fa-file-excel"></i>
                &nbsp; Xuất Excel</span
              >
            </b-dropdown-item>
            <b-dropdown-item @click="importExcel" v-show="canImport">
              <span>
                <i style="font-size: 1rem" class="far fa-file-excel"></i>
                &nbsp; Nhập Excel</span
              >
            </b-dropdown-item>
          </b-dropdown>
          <b-button
            class="font-weight-bolder ml-3"
            @click="createNewLead"
            size="sm"
            variant="primary"
            v-show="canInsert"
          >
            <i style="font-size: 1rem" class="flaticon2-add-1"></i>Thêm mới
          </b-button>
        </b-row>
      </template>

      <!-- Main Content Section -->
      <template v-slot:preview>
        <!-- Filter Section -->
        <b-row class="mb-5">
          <b-col
            md="3"
            class="d-flex align-items-center"
            style="column-gap: 10px"
          >
            <date-picker
              placeholder="Ngày tạo"
              class="form-control form-control-sm w-100"
              v-model="filters.fromDate"
              :config="configs.date"
            >
            </date-picker>
            <date-picker
              placeholder="Ngày chuyển đổi"
              class="form-control form-control-sm w-100"
              v-model="filters.conversionFromDate"
              :config="configs.date"
            >
            </date-picker>
          </b-col>

          <b-col>
            <b-input
              size="sm"
              placeholder="Tìm kiếm ID, Tên, Email"
              v-model.trim="filters.search"
              append-icon="search"
              single-line
              hide-details
              v-on:keyup.enter="onFilter()"
            ></b-input>
          </b-col>

          <b-col>
            <AutoSuggest
              :model="employeeSearchText"
              :suggestions="employeeSuggestions"
              :placeholder="'Người giao cho'"
              :suggestion-name="'label'"
              @change="searchEmployees"
              @select="selectEmployee"
            >
              <div slot="custom" slot-scope="{ suggestion }">
                {{ suggestion.item.label }} - {{ suggestion.item.code }}
              </div>
            </AutoSuggest>
          </b-col>

          <b-col>
            <BDropdownCustom
              v-model="filters.sources"
              :options="sourceOptions"
              :multiple="true"
              :searchable="true"
              value-prop="value"
              text-prop="text"
              placeholder="nguồn"
            />
          </b-col>

          <b-col>
            <BDropdownCustom
              v-model="filters.status"
              :options="statusOptions"
              :multiple="true"
              :searchable="false"
              value-prop="value"
              text-prop="text"
              placeholder="trạng thái"
            />
          </b-col>
        </b-row>

        <div class="mb-5 d-flex justify-content-end">
          <b-button
            style="font-weight: 600; width: 70px"
            variant="primary"
            size="sm"
            @click="onFilter"
            >Lọc</b-button
          >
          <b-button
            style="font-weight: 600; width: 70px"
            size="sm"
            class="ml-2"
            v-b-tooltip.hover
            title="Reset dữ liệu"
            @click="resetFilter"
            variant="secondary"
          >
            <i style="padding-right: 0" class="fa fa-redo"></i>
          </b-button>
        </div>

        <!-- Data Table -->
        <b-table
          :items="leads"
          :fields="fields"
          class="table-bordered table-hover col-md-12"
          no-local-sorting
          :small="true"
          :busy="loading"
        >
          <template v-slot:table-busy>
            <vcl-table :speed="5" :animate="true" :columns="9"></vcl-table>
          </template>

          <!-- Full Name Cell -->
          <template v-slot:cell(leadNumber)="row">
            <div style="cursor: pointer">
              <span
                class="leadNumber text-center d-block"
                @click="editItem(row.item)"
              >
                {{ row.item.leadNumber }}
              </span>
            </div>
          </template>

          <!-- Full Name Cell -->
          <template v-slot:cell(fullName)="row">
            <div style="cursor: pointer">
              <span class="mt-1 mr-1">
                {{ row.item.fullName }}
              </span>
            </div>
          </template>

          <!-- Phone Number Cell -->
          <template v-slot:cell(phoneNumber)="row">
            <div>
              <span
                v-text="row.item.phoneNumber"
                class="d-block white-space"
              ></span>
            </div>
          </template>

          <!-- Email Cell -->
          <template v-slot:cell(email)="row">
            <div>
              <span v-text="row.item.email" class="d-block"></span>
            </div>
          </template>

          <!-- Source Cell -->
          <template v-slot:cell(source)="row">
            <div class="text-center">
              <span v-if="row.item.source" style="width: max-content">
                {{ getSourceText(row.item.source) }}
              </span>
              <span v-else>-</span>
            </div>
          </template>

          <!-- Evaluation Cell -->
          <template v-slot:cell(evaluation)="row">
            <div class="text-center">
              <span
                v-if="row.item.evaluation"
                v-text="getEvaluationText(row.item.evaluation)"
                style="width: max-content"
              ></span>
              <span v-else>-</span>
            </div>
          </template>

          <!-- Assigned To Cell -->
          <template v-slot:cell(assignedTo)="row">
            <div>
              <span v-text="row.item.assignedTo" class="d-block"></span>
            </div>
          </template>

          <!-- Created At Cell -->
          <template v-slot:cell(createdAt)="row">
            <div>
              <span
                v-text="formatDate(row.item.createdAt, 'DD/MM/YYYY')"
                class="d-block"
              ></span>
            </div>
          </template>

          <!-- Status Cell -->
          <template v-slot:cell(status)="row">
            <div class="text-center">
              <span
                v-text="getStatusText(row.item.status)"
                class="label font-weight-bold label-lg label-inline"
                v-bind:class="getStatusClass(row.item.status)"
                style="width: max-content"
              ></span>
            </div>
          </template>

          <!-- Actions Cell -->
          <template v-slot:cell(actions)="row" v-if="haveRowActions">
            <div class="d-flex justify-content-center">
              <b-dropdown size="sm" id="dropdown-left" no-caret right>
                <template slot="button-content">
                  <i
                    style="font-size: 1rem; padding-right: 0px"
                    class="flaticon2-settings"
                  ></i>
                </template>
                <b-dropdown-item @click="editItem(row.item)" v-show="canUpdate">
                  <span style="color: #3f4254; font-size: 12px">
                    <i style="font-size: 1rem" class="flaticon2-pen"></i>
                    &nbsp; Chỉnh sửa
                  </span>
                </b-dropdown-item>
                <b-dropdown-item
                  @click="convertToCustomer(row.item)"
                  v-if="canConvertToCustomer(row.item) && canUpdate"
                >
                  <span style="color: #3f4254; font-size: 12px">
                    <i
                      style="font-size: 1rem; color: #28a745"
                      class="flaticon2-user"
                    ></i>
                    &nbsp; Chuyển đổi khách hàng
                  </span>
                </b-dropdown-item>
                <b-dropdown-item
                  @click="confirmDelete(row.item)"
                  v-show="canDelete"
                >
                  <span style="color: #3f4254; font-size: 12px">
                    <i
                      style="font-size: 1rem; color: #d33"
                      class="flaticon2-rubbish-bin-delete-button"
                    ></i>
                    &nbsp; Xóa
                  </span>
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </template>
        </b-table>

        <!-- Pagination -->
        <b-row>
          <b-col>
            <p class="mt-3 text-dark" style="font-weight: 500">
              Tổng số:
              {{ paginations.total }}
            </p>
          </b-col>
          <b-col>
            <BPaginationNavCustom
              :number-of-pages="paginations.totalPages || 1"
              :current-page="paginations.currentPage"
              :total-page="paginations.total"
              @page-change="onPageChange"
            />
          </b-col>
        </b-row>
      </template>
    </KTCodePreview>

    <!-- Convert Lead Modal -->
    <ConvertLeadModal
      :show="showConvertModal"
      :lead="selectedLead"
      :loading="convertLoading"
      @close="closeConvertModal"
      @convert="handleLeadConversion"
    />
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import BPaginationNavCustom from '@/view/base/bootstrap/BPaginationNavCustom.vue';
import BDropdownCustom from '@/view/base/bootstrap/BDropdownCustom.vue';
import AutoSuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import ConvertLeadModal from './components/ConvertLeadModal.vue';

import { DP_CONFIG, formatDate, formatDBDate } from '@/utils/date';
import { createFile, getFileName } from '@/utils/file-download';

import { swalMixin } from '@/view/mixins';
import { permissionMixin } from '@/view/mixins/permission.mixins';

import LeadService from '@/core/api/lead/lead.service';
import UserModel from '@/core/models/User.model';
import { LeadModel } from '@/core/models/Lead.model';
import { UserService } from '@/core/api/user/user.service';

import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';

import { makeToastFaile, makeToastSuccess } from '@/utils/common';
import { VclTable } from 'vue-content-loading';

import * as ENUM_NAME from '@/utils/enum-name';
import * as ENUM from '@/utils/enum';
import * as _ from 'lodash';

export default {
  name: 'ListLead',
  mixins: [swalMixin, permissionMixin],
  components: {
    KTCodePreview,
    VclTable,
    BPaginationNavCustom,
    BDropdownCustom,
    AutoSuggest,
    ConvertLeadModal,
  },
  data() {
    return {
      leads: [],
      loading: false,
      perPage: 10,
      filters: {
        search: '',
        sources: [],
        status: [],
        assignedUserId: null,
        fromDate: '',
        toDate: '',
        conversionFromDate: '',
      },

      employeeSuggestions: [],
      employeeSearchText: '',
      searchTimeout: null,

      showConvertModal: false,
      selectedLead: null,
      convertLoading: false,

      paginations: {
        total: 0,
        currentPage: 1,
        totalPages: 1,
      },

      // Configs
      configs: DP_CONFIG,
      PERMISSIONS: ENUM.PERMISSIONS,

      fields: [
        {
          key: 'leadNumber',
          label: 'ID',

          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'fullName',
          label: 'Họ và tên',

          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'phoneNumber',
          label: 'SĐT',

          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'email',
          label: 'Email',

          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'source',
          label: 'Nguồn',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'evaluation',
          label: 'Đánh giá',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'status',
          label: 'Trạng thái',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'assignedTo',
          label: 'Giao cho',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'createdAt',
          label: 'Ngày tạo',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
      ],
      sourceOptions: this.getOptions(
        ENUM.LEAD_SOURCE,
        ENUM_NAME.LEAD_SOURCE_NAME,
      ),
      statusOptions: this.getOptions(
        ENUM.LEAD_STATUS,
        ENUM_NAME.LEAD_STATUS_NAME,
      ),
      evaluationOptions: this.getOptions(
        ENUM.LEAD_EVALUATION,
        ENUM_NAME.LEAD_EVALUATION_NAME,
      ),
    };
  },

  created() {
    if (!this.canViewOrViewAll) {
      this.$router.replace({ name: 'dashboard' });
    }

    if (this.haveRowActions) {
      this.fields.push({
        key: 'actions',
        label: '',
        sortable: false,
        thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
      });
    }
  },

  mounted() {
    this.setBreadCrumbs();
    this.fetchData();
  },

  computed: {
    canViewOnly() {
      return this.hasPermission(this.PERMISSIONS.LEAD_VIEW_ONLY);
    },
    canViewAll() {
      return this.hasPermission(this.PERMISSIONS.LEAD_VIEW_ALL);
    },
    canViewOrViewAll() {
      return this.hasAnyOfPermissions([
        this.PERMISSIONS.LEAD_VIEW_ONLY,
        this.PERMISSIONS.LEAD_VIEW_ALL,
      ]);
    },
    canInsert() {
      return this.hasPermission(this.PERMISSIONS.LEAD_INSERT);
    },
    canUpdate() {
      return this.hasPermission(this.PERMISSIONS.LEAD_UPDATE);
    },
    canDelete() {
      return this.hasPermission(this.PERMISSIONS.LEAD_DELETE);
    },
    canImport() {
      return this.hasPermission(this.PERMISSIONS.LEAD_IMPORT);
    },
    canExport() {
      return this.hasPermission(this.PERMISSIONS.LEAD_EXPORT);
    },
    haveRowActions() {
      return this.canUpdate || this.canDelete;
    },
    haveHeaderActions() {
      return this.canInsert || this.canImport || this.canExport;
    },
  },

  methods: {
    makeToastSuccess,
    makeToastFaile,
    formatDate,

    setBreadCrumbs() {
      this.$store.dispatch(SET_BREADCRUMB, [
        { title: 'Leads', route: '/crm/leads' },
        { title: 'Danh sách', active: true },
      ]);
    },

    getOptions(enumValues, enumNames) {
      if (!enumValues || !enumNames) return [];
      if (!Object.values(enumValues).length) return [];

      const options = Object.values(enumValues).map((val) => ({
        value: val,
        text: enumNames[val] || val,
      }));

      return options;
    },

    normalizer(node) {
      return {
        id: node.value,
        label: node.text,
      };
    },

    selectEmployee(option) {
      if (option?.item) {
        this.filters.assignedUserId = option.item.id;
        this.employeeSearchText = option.item.label;
      } else {
        this.filters.assignedUserId = null;
        this.employeeSearchText = '';
      }
    },

    searchEmployees(searchQuery) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      this.searchTimeout = setTimeout(() => {
        this.performSearch(searchQuery);
      }, 300);
    },

    async performSearch(searchQuery) {
      this.employeeSuggestions = [];

      if (!searchQuery || searchQuery.length < 2) {
        return;
      }

      try {
        const { data } = await UserService.smartSearch({
          keyword: searchQuery,
          limit: 5,
        });

        const employees = UserModel.fromJsonArray(data?.data.data) || [];
        this.employeeSuggestions = employees.map((employee) => ({
          id: employee.id,
          label: employee.fullName,
          name: employee.fullName,
          code: employee.code,
        }));
      } catch (error) {
        console.error('Error loading employees:', error);
      }
    },

    async fetchData(page = 1) {
      this.loading = true;
      try {
        const sourceArray = this.filters.sources.map((source) =>
          Number(source.value),
        );
        const statusArray = Array.isArray(this.filters.status)
          ? this.filters.status.map((s) => +s.value)
          : [];
        const fromDate = this.filters.fromDate
          ? formatDBDate(this.filters.fromDate)
          : null;
        const toDate = this.filters.toDate
          ? formatDBDate(this.filters.toDate)
          : null;
        const conversionFromDate = this.filters.conversionFromDate
          ? formatDBDate(this.filters.conversionFromDate)
          : null;

        const params = {
          ...this.filters,
          sources: sourceArray,
          status: statusArray,
          fromDate,
          toDate,
          conversionFromDate,
          page: page,
          limit: this.perPage,
        };

        const { data } = await LeadService.getAll(
          _.omitBy(params, (value) => !value),
        );
        this.leads = LeadModel.fromJsonArray(data.data.data) || [];
        this.paginations = data.data.paginations;
      } catch (error) {
        console.error('Error fetching leads:', error);
        makeToastFaile('Có lỗi xảy ra khi tải dữ liệu');
      } finally {
        this.loading = false;
      }
    },
    onFilter() {
      this.fetchData();
    },
    resetFilter() {
      this.filters = {
        search: '',
        sources: [],
        status: [],
        assignedUserId: null,
        fromDate: '',
        toDate: '',
        conversionFromDate: '',
      };
      this.employeeSearchText = '';
      this.resetPagination();
      this.fetchData();
    },
    resetPagination() {
      this.paginations = {
        total: 0,
        currentPage: 1,
        totalPages: 1,
      };
    },
    onPageChange(page) {
      this.fetchData(page);
    },
    getStatusClass(status) {
      const statusMap = {
        [ENUM.LEAD_STATUS.INTERESTED]: 'label-light-primary',
        [ENUM.LEAD_STATUS.NOT_INTERESTED]: 'label-light-danger',
        [ENUM.LEAD_STATUS.POTENTIAL]: 'label-light-warning',
        [ENUM.LEAD_STATUS.PURCHASED]: 'label-light-success',
      };
      return statusMap[status] || 'label-light-warning';
    },
    getStatusText(status) {
      return ENUM_NAME.LEAD_STATUS_NAME[status] || status || 'Chưa xác định';
    },
    getSourceText(source) {
      return ENUM_NAME.LEAD_SOURCE_NAME[source] || source || 'Chưa xác định';
    },
    getEvaluationClass(evaluation) {
      const evaluationMap = {
        [ENUM.LEAD_EVALUATION.NOT_CONTACTED]: 'label-light-warning',
        [ENUM.LEAD_EVALUATION.CONTACTED]: 'label-light-info',
        [ENUM.LEAD_EVALUATION.CONVERTED]: 'label-light-success',
      };
      return evaluationMap[evaluation] || 'label-light-secondary';
    },
    getEvaluationText(evaluation) {
      return (
        ENUM_NAME.LEAD_EVALUATION_NAME[evaluation] ||
        evaluation ||
        'Chưa có đánh giá'
      );
    },
    canConvertToCustomer(lead) {
      // Individual customer: require name + phone
      // Business customer: require name + phone + tax code + company name
      const hasBasicInfo = lead.fullName && lead.phoneNumber;

      if (
        lead.customerGroup === ENUM.CUSTOMER_GROUP.OLD &&
        lead.taxCode &&
        lead.companyName
      ) {
        // Business customer - need all required fields
        return hasBasicInfo && lead.taxCode && lead.companyName;
      }

      return hasBasicInfo;
    },
    convertToCustomer(lead) {
      this.selectedLead = lead;
      this.showConvertModal = true;
    },
    closeConvertModal() {
      this.showConvertModal = false;
      this.selectedLead = null;
      this.convertLoading = false;
    },
    async handleLeadConversion(data) {
      try {
        this.convertLoading = true;
        const { customerType, leadData } = data;

        await LeadService.convertToCustomer(customerType, leadData);
        makeToastSuccess('Chuyển đổi khách hàng thành công');
        this.closeConvertModal();
        await this.fetchData();
      } catch (error) {
        console.error('Error converting to customer:', error);
        const errorMessage =
          error?.response?.data?.message ||
          'Có lỗi xảy ra khi chuyển đổi khách hàng';
        makeToastFaile(errorMessage);
        this.convertLoading = false;
      }
    },
    createNewLead() {
      this.$router.push({
        name: 'add-lead',
      });
    },
    editItem(item) {
      if (!this.canUpdate) {
        this.$router.push({
          name: 'view-lead',
          params: { id: item.id },
        });
        return;
      }
      this.$router.push({
        name: 'edit-lead',
        params: { id: item.id },
      });
    },

    async confirmDelete(item) {
      const result = await this.popupSwalWarning({
        title: 'Xác nhận xóa',
        text: `Bạn có chắc chắn muốn xóa lead "${item.fullName}"?`,
      });

      if (result.isConfirmed) {
        await this.deleteItem(item.id);
      }
    },
    async deleteItem(id) {
      try {
        await LeadService.delete(id);
        makeToastSuccess('Xóa lead thành công');
        this.fetchData();
      } catch (error) {
        console.error('Error deleting lead:', error);
        makeToastFaile('Có lỗi xảy ra khi xóa lead');
      }
    },

    async exportExcel() {
      try {
        const sourceArray = this.filters.sources.map((source) =>
          Number(source.value),
        );
        const fromDate = this.filters.fromDate
          ? formatDBDate(this.filters.fromDate)
          : null;
        const toDate = this.filters.toDate
          ? formatDBDate(this.filters.toDate)
          : null;

        const params = {
          ...this.filters,
          sources: sourceArray,
          fromDate,
          toDate,
        };
        const response = await LeadService.exportExcel(
          _.omitBy(params, (value) => !value),
        );
        const fileName = getFileName(response) || 'danh-sach-lead.xlsx';
        createFile(response.data, fileName);
        makeToastSuccess('Xuất Excel thành công');
      } catch (error) {
        console.error('Error exporting excel:', error);
        makeToastFaile('Có lỗi xảy ra khi xuất Excel');
      }
    },
    importExcel() {
      this.$router.push({
        name: 'import-lead',
      });
    },
  },
};
</script>

<style scoped>
.list-lead {
  .labelInput {
    font-weight: 600;
  }
  .leadNumber {
    color: #3699ff;
  }

  .leadNumber:hover {
    text-decoration: underline;
    cursor: pointer;
  }

  .inputText {
    width: 22%;
  }
  .btn.btn-icon.btn-sm {
    height: calc(1.35em + 1.1rem + 2px);
    width: auto;
  }

  .icon:hover {
    background-color: #90c6fc;
  }

  .dropdown-header {
    color: #3699ff !important;
  }
  .table th,
  .table td {
    padding: 0.75rem;
    vertical-align: middle;
    border-top: 1px solid #ecf0f3;
  }
  .table.b-table.table-sm
    > thead
    > tr
    > [aria-sort]:not(.b-table-sort-icon-left),
  .table.b-table.table-sm
    > tfoot
    > tr
    > [aria-sort]:not(.b-table-sort-icon-left) {
    background-position: right calc(1.5rem / 2) center;
    padding-right: calc(0.3rem + 0.65em);
  }

  .page-item.disabled {
    cursor: not-allowed;
    &.bv-d-xs-down-none {
      .page-link {
        margin: 0.35rem 0.25rem;
        text-align: center;
      }
    }
  }

  .form-group label {
    font-weight: 500;
  }

  .white-space {
    white-space: pre-wrap;
  }

  /* Sources cell styling */
  .sources-list {
    display: flex;
    flex-direction: column;
  }

  .source-item {
    padding: 2px 0;
    text-align: center;
    margin-bottom: 4px;
    border-bottom: 1px solid #f0f0f0;
  }

  .source-item:last-child {
    margin-bottom: 0;
    border-bottom: none;
  }
}
</style>
